// The top-level crash screen. Rendered by Sentry.ErrorBoundary's fallback in
// src/index.js when a page throws during render.
//
// WHAT THIS REPLACED, AND WHY
// It used to be literally `<p>Something went wrong. Please refresh the page.</p>`.
// Two problems, both of which bit on build e122663 when /takeoff crashed:
//   1. It named nothing. The owner could not tell which page had failed, and
//      neither could anyone reading his description of it.
//   2. "Refresh the page" is not a gesture the native Android app has — no
//      address bar, no reload button, no pull-to-refresh. The only way out was
//      to kill the app from the task switcher.
// So: name the page, offer a real Retry, and — because a retry on a page that
// crashes during render just crashes again — always offer a way OFF that page.
//
// Deliberately dependency-free: no react-router hooks (this sits ABOVE the
// Router, and the Router may be what threw), no firebase, no app context. It
// reads window.location directly and navigates by assigning to it.
import React, { useState } from 'react';
import { pageLabelFromPath } from '../utils/pageLabel';

const BUILD_SHA = process.env.REACT_APP_BUILD_SHA || 'dev';

const wrap = {
  minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: 20, background: 'var(--dark1, #15171c)', color: 'var(--text, #e8eaed)',
  fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
};
const card = {
  width: '100%', maxWidth: 440, background: 'var(--dark2, #1c1f26)',
  border: '1px solid var(--border, #2c3038)', borderRadius: 10, padding: 22, textAlign: 'center',
};
const btn = {
  width: '100%', padding: '13px 16px', borderRadius: 8, fontSize: 15, fontWeight: 600,
  cursor: 'pointer', border: '1px solid var(--border, #2c3038)', marginTop: 10,
  background: 'var(--dark3, #23262e)', color: 'var(--text, #e8eaed)',
};
const primaryBtn = { ...btn, background: '#2d6cdf', borderColor: '#2d6cdf', color: '#fff' };

export default function AppErrorScreen({ error, resetError, eventId }) {
  const [tries, setTries] = useState(0);
  const path = (typeof window !== 'undefined' && window.location && window.location.pathname) || '';
  const page = pageLabelFromPath(path);
  const named = /^[a-z]/.test(page) ? page : `the ${page} page`;   // "the signing page" vs "the Takeoff page"
  const keepsFailing = tries >= 1;

  const detail = (() => {
    const m = error && (error.message || String(error));
    return m ? String(m).slice(0, 180) : '';
  })();

  const retry = () => { setTries(t => t + 1); if (typeof resetError === 'function') resetError(); };
  const go = (to) => () => { try { window.location.assign(to); } catch (_) { window.location.href = to; } };
  const reload = () => { try { window.location.reload(); } catch (_) { /* nothing else to try */ } };

  return (
    <div style={wrap}>
      <div style={card} role="alert">
        <div style={{ fontSize: 34, lineHeight: 1 }}>⚠️</div>
        <h2 style={{ margin: '12px 0 6px', fontSize: 19 }}>
          {keepsFailing ? `${named.charAt(0).toUpperCase() + named.slice(1)} keeps failing` : `${named.charAt(0).toUpperCase() + named.slice(1)} hit an error`}
        </h2>
        <p style={{ margin: '0 0 4px', fontSize: 14, color: 'var(--text2, #a7adb8)', lineHeight: 1.45 }}>
          {keepsFailing
            ? 'Nothing you did caused this, and nothing you had saved is lost. Open a different page and send this screen to Landon.'
            : 'Nothing you had already saved is lost. Tap Try again — if it fails a second time, open a different page instead.'}
        </p>
        {detail && (
          <p style={{ margin: '10px 0 0', fontSize: 11, color: 'var(--text2, #a7adb8)', fontFamily: 'ui-monospace, Menlo, monospace', wordBreak: 'break-word' }}>
            {detail}
          </p>
        )}

        {!keepsFailing && <button style={primaryBtn} onClick={retry}>Try again</button>}
        <button style={keepsFailing ? primaryBtn : btn} onClick={go('/dashboard')}>Go to Dashboard</button>
        <button style={btn} onClick={go('/jobs')}>Go to Jobs</button>
        <button style={btn} onClick={reload}>Restart RoofBeam</button>

        <p style={{ margin: '14px 0 0', fontSize: 11, color: 'var(--text2, #a7adb8)' }}>
          {path || '/'} · build {BUILD_SHA}{eventId ? ` · ${String(eventId).slice(0, 8)}` : ''}
        </p>
      </div>
    </div>
  );
}
